import React, { useMemo } from 'react';
import styled from '@emotion/styled';

import closeIcon from 'assets/images/icons/Close.svg';
import { Icon } from 'components/shared/Buttons';
import { theme } from 'theme';

const SectionCell = styled.div<{ noPadding?: boolean }>`
  padding: ${({ noPadding }) => (noPadding ? 0 : '0 32px')};
  display: block;
`;

const FieldSet = styled.fieldset`
  padding: 0;
  border: none;
  margin-left: 0;
  margin-right: 0;
  padding: 0 0 16px;
  border-bottom: 1px solid ${theme.colors.mineShaftLightest};
`;

const SectionHeader = styled.div<{ noPadding?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${({ noPadding }) => (noPadding ? '25px 0 0' : '25px 32px 0')};
  align-items: center;
  height: 50px;

  legend {
    font-size: 12px;
    font-weight: bold;
    color: ${theme.colors.alphaGray};
  }

  span:last-of-type {
    height: 15px;
    width: 15px;
    cursor: pointer;
  }
`;

const SectionContent = styled.div`
  font-family: ${theme.fonts.montserrat};
  display: grid;
  grid-template-columns: 1fr;
  gap: 1px;
`;

interface Props {
  children?: React.ReactNode;
  label?: string | React.ReactNode;
  onSectionClear?: React.MouseEventHandler;
  clearable?: boolean;
  noPadding?: boolean;
  // sectionName is used for mapping in <FiltersForm />
  // eslint-disable-next-line react/no-unused-prop-types
  sectionName?: string;
}

export const FiltersSection = ({
  children,
  label,
  onSectionClear,
  clearable,
  noPadding,
}: Props): React.ReactElement => {
  const filteredChildren = useMemo(
    () => React.Children.toArray(children).filter(child => !!child),
    [children],
  );

  return (
    <FieldSet>
      {(label || clearable) && (
        <SectionHeader noPadding={noPadding}>
          <legend>{label}</legend>
          {clearable && (
            <Icon
              icon={closeIcon}
              onClick={onSectionClear}
              data-cy="filters-section-clear-button"
            />
          )}
        </SectionHeader>
      )}
      <SectionContent>
        {filteredChildren.map((child, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <SectionCell key={index} noPadding={noPadding}>
            {child}
          </SectionCell>
        ))}
      </SectionContent>
    </FieldSet>
  );
};
